import { useI18n } from 'vue-i18n'

const NS = 'visualGameAdmin.novelRuns.audit'

/** 对局审计页共用的取词：枚举取值、故事时间、来源轮次、证据来源、记忆种类、错误码、用量与成本。 */
export function useAuditText() {
  const { t, te } = useI18n()
  const tr = (key, params) => t(`${NS}.${key}`, params ?? {})
  /** 带兜底的枚举文案：没有翻译的取值（比如以后新增的任务种类）显示原值。 */
  const enumLabel = (group, value) => (te(`${NS}.${group}.${value}`) ? tr(`${group}.${value}`) : String(value ?? '—'))

  function storyTimeLabel(value) {
    if (!value || typeof value !== 'object') return '—'
    const phase = value.phase ? enumLabel('time.phases', value.phase) : '—'
    return `${tr('time.day', { day: value.day ?? 0 })} · ${phase}`
  }

  /** 记录覆盖的轮次：单轮写"第 N 轮"，区间写"第 a–b 轮"。 */
  function rangeLabel(record) {
    const from = record.from_seq ?? record.based_on_from_seq ?? record.seq ?? 0
    const through = record.through_seq ?? record.based_on_through_seq ?? record.seq ?? from
    return from === through ? tr('sources.turn', { count: through }) : tr('sources.range', { from, through })
  }

  /** 证据出处只写轮次或来源种类；证据 id 由调用方放进 title。 */
  function sourceLabel(source) {
    return source.seq > 0 ? tr('sources.turn', { count: source.seq }) : enumLabel('sources.kinds', source.source_kind)
  }

  function memoryKindLabel(record) {
    if (record.record_source === 'correction') return tr('memoryKinds.correction')
    if (record.record_source === 'conflict') return tr('memoryKinds.conflict')
    return te(`${NS}.memoryKinds.${record.kind}`) ? tr(`memoryKinds.${record.kind}`) : tr('memoryKinds.record')
  }

  /** 错误码转文案：收录的取对应文案，带三位状态码后缀的按前缀取；没收录的写"未归类错误"，原码由调用方放进 title。 */
  function errorLabel(code) {
    if (!code) return '—'
    if (te(`${NS}.errors.${code}`)) return tr(`errors.${code}`)
    const suffixed = /^(.+)_(\d{3})$/.exec(code)
    if (suffixed && te(`${NS}.errors.${suffixed[1]}`)) return `${tr(`errors.${suffixed[1]}`)} · ${suffixed[2]}`
    return tr('errors.unknown')
  }

  /** 平台审计里的 token 用量（输入 / 输出）；平台查不到这次调用时为 —。 */
  function tokensLabel(call) {
    if (!call || (call.prompt_tokens === null && call.completion_tokens === null)) return '—'
    return `${call.prompt_tokens ?? '—'} / ${call.completion_tokens ?? '—'}`
  }

  /** 成本：按币种列出供应商成本，再加扣点；都没有时为 —。totals 取 callTotals() 的形状。 */
  function costLabel(totals) {
    const parts = []
    for (const [currency, micros] of Object.entries(totals.cost_micros)) parts.push(moneyLabel(micros, currency))
    if (totals.charged_points > 0) parts.push(tr('points', { points: totals.charged_points }))
    return parts.length > 0 ? parts.join(' · ') : '—'
  }

  return { t, tr, enumLabel, storyTimeLabel, rangeLabel, sourceLabel, memoryKindLabel, errorLabel, tokensLabel, costLabel }
}

const pad = (value) => String(value).padStart(2, '0')

/** 时刻只显示本地 HH:mm:ss；完整时间用 fullTime 放进 title。 */
export function clockTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function fullTime(value) {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString()
}

/** 耗时：不到一秒写 "293 ms"，否则写 "6.1 s"。 */
export function duration(ms) {
  if (ms === null || ms === undefined) return '—'
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`
}

export function elapsedMs(started_at, completed_at) {
  const start = new Date(started_at).getTime()
  const end = new Date(completed_at).getTime()
  return Number.isFinite(start) && Number.isFinite(end) && end >= start ? end - start : null
}

/** 供应商成本以百万分之一货币单位记账。 */
export function moneyLabel(micros, currency) {
  return `${(micros / 1_000_000).toFixed(4)} ${currency}`.trim()
}

export function pretty(value) {
  return JSON.stringify(value ?? null, null, 2)
}

export function shortId(id) {
  return id ? String(id).slice(0, 8) : ''
}

export function clip(text, limit = 160) {
  const value = String(text ?? '')
  return value.length > limit ? `${value.slice(0, limit)}…` : value
}

const STATUS_VARIANTS = {
  active: 'success',
  completed: 'success',
  committed: 'success',
  ok: 'success',
  pending: 'warning',
  waiting: 'warning',
  generating: 'warning',
  started: 'warning',
  aborted: 'warning',
  failed: 'danger',
  error: 'danger',
  superseded: 'neutral',
  cancelled: 'neutral',
  archived: 'neutral',
}

/** 状态芯片颜色：完成类 success，进行中类 warning，失败类 danger，作废、取消与未知 neutral。 */
export function variantOf(status) {
  return STATUS_VARIANTS[status] ?? 'neutral'
}

/** 任务覆盖的轮次：单轮写 #N，区间写 #from–through。 */
export function jobRange(job) {
  const through = job.through_seq
  if (through === undefined || through === null) return '—'
  const from = job.from_seq
  return from === undefined || from >= through ? `#${through}` : `#${from}–${through}`
}
